import React, { useEffect, useState } from 'react'
import {
  useStripe,
  useElements,
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import { Button, Form, Alert, Modal } from 'react-bootstrap'
import useAddPaymentIntent from 'hooks/use-add-payment-intent'
import toast from 'react-hot-toast'
import { Stripe } from '@stripe/stripe-js'

interface AddCardFormProps {
  onCancel: () => void
  onSuccess: (paymentMethodId: string) => void
  customerId: string
  organizationId?: string
  show: boolean
  stripePromise: Promise<Stripe | null>
  name?: string
  email?: string
}

// Separate component that uses Stripe hooks inside Elements context
const StripeForm: React.FC<{
  onCancel: () => void
  onSuccess: (paymentMethodId: string) => void
  customerId: string
  organizationId?: string
}> = ({ onCancel, onSuccess, customerId, organizationId }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const { mutate: addPaymentIntent, isLoading: isAddPaymentIntentLoading } =
    useAddPaymentIntent(organizationId)

  const isLoading = isAddPaymentIntentLoading

  const handlePaymentMethodSetup = async (clientSecret: string) => {
    if (!stripe || !elements) {
      return toast.error('Error loading Stripe. Please refresh the page')
    }

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement)!,
      },
    })
    if (result.error) {
      setError(result.error.message || 'Failed to add card')
    } else {
      const paymentMethodId = result.setupIntent.payment_method
      toast.success('Payment method created successfully!')
      onSuccess(paymentMethodId as string)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return toast.error('Error loading Stripe. Please refresh the page')
    }
    if (organizationId) {
      addPaymentIntent(
        {
          data: {
            customerId: customerId,
            intent: 'subscription_payment_method',
            usage: 'off_session',
          },
        },
        {
          onSuccess: async (data) => {
            handlePaymentMethodSetup(data.client_secret)
          },
        }
      )
    }
  }

  return (
    <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
      <Form.Group>
        <Form.Label className="text-white small mb-2 bg-none">
          Card Number
        </Form.Label>

        <div
          className="border p-3"
          style={{
            borderRadius: '8px',
          }}
        >
          <CardNumberElement
            options={{
              style: {
                base: {
                  fontSize: '14px',
                  color: 'white',
                  '::placeholder': {
                    color: 'white',
                    iconColor: 'white',
                  },
                },
                invalid: {
                  color: '#fa755a',
                  iconColor: '#fa755a',
                },
              },
            }}
          />
        </div>
      </Form.Group>
      <div className="d-flex gap-3 justify-content-between">
        <Form.Group className="mb-3 flex-fill">
          <Form.Label className="text-white small mb-2 bg-none">
            Exp Date
          </Form.Label>

          <div
            className="border p-3"
            style={{
              borderRadius: '8px',
            }}
          >
            <CardExpiryElement
              options={{
                style: {
                  base: {
                    fontSize: '14px',
                    color: 'white',
                    '::placeholder': {
                      color: 'white',
                      iconColor: 'white',
                    },
                  },
                  invalid: {
                    color: '#fa755a',
                    iconColor: '#fa755a',
                  },
                },
              }}
            />
          </div>
        </Form.Group>
        <Form.Group className="mb-3 flex-fill">
          <Form.Label className="text-white small mb-2 bg-none">CVC</Form.Label>

          <div
            className="border p-3"
            style={{
              borderRadius: '8px',
            }}
          >
            <CardCvcElement
              options={{
                style: {
                  base: {
                    fontSize: '14px',
                    color: 'white',
                    '::placeholder': {
                      color: 'white',
                      iconColor: 'white',
                    },
                  },
                },
              }}
            />
          </div>
        </Form.Group>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex gap-3 justify-content-end">
        <Button
          variant="outline-primary"
          type="button"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading || !stripe}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Form>
  )
}

const AddPaymentMethodModal: React.FC<AddCardFormProps> = ({
  onCancel,
  onSuccess,
  customerId,
  stripePromise,
  show,
  organizationId,
  name: nameProp,
  email: emailProp,
}) => {
  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Body>
        <div style={{ fontSize: '24px', marginBottom: '20px' }}>
          Add Payment Information
        </div>
        {stripePromise && (
          <Elements stripe={stripePromise}>
            <StripeForm
              onCancel={onCancel}
              onSuccess={onSuccess}
              customerId={customerId}
              organizationId={organizationId}
            />
          </Elements>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default AddPaymentMethodModal
